import React, { useEffect, useState, useMemo, Fragment } from 'react';
import Breadcrumb from '../../Breadcrumb/Breadcrumb';
import axios from '../../../axios';
import usePermisos from '../../../hooks/usePermisos';
import NoPermissions from '../../NoPermissions/NoPermissions';
import Loader from '../../../Layout/Loaders/Loader';
import Spinner from '../../../Layout/Loaders/Spinner';
import notify from '../../../Utils/notify';
import { Dialog, DialogTitle, Transition, DialogBackdrop, DialogPanel, Switch } from '@headlessui/react';
import {
    PlusIcon,
    XMarkIcon,
    TrashIcon,
    ClockIcon,
    CalendarDaysIcon,
    CheckIcon,
    DocumentDuplicateIcon,
    SparklesIcon,
    BoltIcon
} from '@heroicons/react/24/outline';

const DIAS = [
    { id: 1, nombre: 'Lunes' },
    { id: 2, nombre: 'Martes' },
    { id: 3, nombre: 'Miércoles' },
    { id: 4, nombre: 'Jueves' },
    { id: 5, nombre: 'Viernes' },
    { id: 6, nombre: 'Sábado' },
    { id: 7, nombre: 'Domingo' }
];

const INTERVALOS = [
    { value: 10, label: '10 minutos' },
    { value: 15, label: '15 minutos' },
    { value: 20, label: '20 minutos' },
    { value: 30, label: '30 minutos' },
    { value: 45, label: '45 minutos' },
    { value: 60, label: '1 hora' }
];

const PERMISOS_REQUERIDOS = [
    'appointment_models_create',
    'appointment_models_list_read',
    'appointment_models_update',
    'appointment_models_delete'
];

const ModeloCitas = () => {
    const { loading: loadingPermisos, tienePermiso } = usePermisos(PERMISOS_REQUERIDOS);
    const [sucursales, setSucursales] = useState([]);
    const [diaSeleccionado, setDiaSeleccionado] = useState(1);
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
    const [citas, setCitas] = useState([]);
    const [loading, setLoading] = useState(false);

    // Estado para el modal de creación masiva
    const [modalOpen, setModalOpen] = useState(false);
    const [diaActivo, setDiaActivo] = useState(1); // Día actualmente siendo editado en el modal
    const [horasPorDia, setHorasPorDia] = useState({}); // { 1: ['08:00', '09:00'], 2: ['10:00'], ... }
    const [horaInput, setHoraInput] = useState('');
    const [observacionesModal, setObservacionesModal] = useState('');
    const [creando, setCreando] = useState(false);
    const [mensajeExito, setMensajeExito] = useState('');

    // Estado para generación automática
    const [modoAutomatico, setModoAutomatico] = useState(false);
    const [horaInicio, setHoraInicio] = useState('08:00');
    const [horaFin, setHoraFin] = useState('18:00');
    const [intervalo, setIntervalo] = useState(30);

    // Estado para copiar día
    const [showCopiarMenu, setShowCopiarMenu] = useState(false);

    useEffect(() => {
        getSucursales();
        const sucursal = localStorage.getItem('sucursal_selected');
        if (sucursal) {
            setSucursalSeleccionada(sucursal);
        }
    }, []);

    useEffect(() => {
        if (diaSeleccionado > 0 && sucursalSeleccionada > 0) {
            getCitas();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sucursalSeleccionada, diaSeleccionado]);

    const getSucursales = () => {
        axios.get('api/sucursals')
            .then(res => setSucursales(res.data))
            .catch(e => console.log(e, 'Error al obtener sucursales'));
    };

    const getCitas = () => {
        setLoading(true);
        axios.post('api/get_appointment_models', { sucursal_id: sucursalSeleccionada, day_id: diaSeleccionado })
            .then(res => {
                setCitas(res.data);
                setLoading(false);
            })
            .catch(e => {
                console.log(e, 'Error al obtener modelos');
                setLoading(false);
                notify.error('Error al cargar los modelos de citas');
            });
    };

    const getSucursalNombre = (sucursalId) => {
        const suc = sucursales.find(s => s.id === parseInt(sucursalId));
        return suc ? suc.nombre_sucursal : '';
    };

    // Actualizar el valor local del input (sin llamar a la API)
    const handleInputChange = (field, value, citaId) => {
        setCitas(prev => prev.map(c => c.id === citaId ? { ...c, [field]: value } : c));
    };

    // Guardar cambios en la base de datos al perder el foco
    const handleBlur = (field, value, cita) => {
        const newCita = { ...cita, [field]: value };
        console.log("Actualizando cita:", newCita);
        axios.put(`api/appointment_model/${cita.id}`, newCita)
            .then(res => {
                if (res.status === 200) {
                    notify.success('Horario actualizado');
                }
            })
            .catch(e => {
                console.log("Error:", e);
                notify.error('Error al actualizar el horario');
                // Revertir cambio en caso de error
                getCitas();
            });
    };

    const handleDelete = (cita) => {
        if (window.confirm("¿Realmente deseas borrar este horario de cita?")) {
            axios.delete(`api/appointment_model/${cita.id}`)
                .then(res => {
                    if (res.status === 200) {
                        setCitas(citas.filter(c => c.id !== res.data.modelo.id));
                        notify.success('Horario eliminado correctamente');
                    }
                })
                .catch(e => {
                    console.log("error", e);
                    notify.error('Error al eliminar el horario');
                });
        }
    };

    // ===== Lógica del Modal de Creación Masiva =====

    const getHorasDelDia = (diaId) => horasPorDia[diaId] || [];

    const agregarHora = () => {
        if (!horaInput) return;
        const horasActuales = getHorasDelDia(diaActivo);
        if (horasActuales.includes(horaInput)) {
            alert('Esta hora ya fue agregada para este día');
            return;
        }
        setHorasPorDia({
            ...horasPorDia,
            [diaActivo]: [...horasActuales, horaInput].sort()
        });
        setHoraInput('');
    };

    const eliminarHora = (hora) => {
        const horasActuales = getHorasDelDia(diaActivo);
        setHorasPorDia({
            ...horasPorDia,
            [diaActivo]: horasActuales.filter(h => h !== hora)
        });
    };

    const limpiarHorasDia = () => {
        setHorasPorDia({
            ...horasPorDia,
            [diaActivo]: []
        });
    };

    // Generar horarios automáticos
    const generarHorariosAutomaticos = () => {
        const horas = [];
        const [inicioH, inicioM] = horaInicio.split(':').map(Number);
        const [finH, finM] = horaFin.split(':').map(Number);

        let currentMinutes = inicioH * 60 + inicioM;
        const endMinutes = finH * 60 + finM;

        while (currentMinutes <= endMinutes) {
            const hours = Math.floor(currentMinutes / 60);
            const mins = currentMinutes % 60;
            horas.push(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
            currentMinutes += intervalo;
        }

        setHorasPorDia({
            ...horasPorDia,
            [diaActivo]: horas
        });
    };

    // Copiar horarios de un día a otro
    const copiarHorasADia = (diaDestinoId) => {
        const horasOrigen = getHorasDelDia(diaActivo);
        if (horasOrigen.length === 0) {
            alert('No hay horarios para copiar en el día actual');
            return;
        }
        setHorasPorDia({
            ...horasPorDia,
            [diaDestinoId]: [...horasOrigen]
        });
        setShowCopiarMenu(false);
    };

    // Copiar a todos los días
    const copiarATodosLosDias = () => {
        const horasOrigen = getHorasDelDia(diaActivo);
        if (horasOrigen.length === 0) {
            alert('No hay horarios para copiar en el día actual');
            return;
        }
        const nuevasHoras = {};
        DIAS.forEach(dia => {
            nuevasHoras[dia.id] = [...horasOrigen];
        });
        setHorasPorDia(nuevasHoras);
        setShowCopiarMenu(false);
    };

    // Contar días con horarios
    const diasConHorarios = useMemo(() => {
        return DIAS.filter(dia => (horasPorDia[dia.id] || []).length > 0);
    }, [horasPorDia]);

    // Preview de slots a crear
    const previewSlots = useMemo(() => {
        const slots = [];
        Object.entries(horasPorDia).forEach(([diaId, horas]) => {
            const dia = DIAS.find(d => d.id === parseInt(diaId));
            if (dia && horas.length > 0) {
                horas.forEach(hora => {
                    slots.push({
                        dia: dia.nombre,
                        diaId: parseInt(diaId),
                        hora: hora
                    });
                });
            }
        });
        return slots;
    }, [horasPorDia]);

    const totalSlots = previewSlots.length;
    const canSubmit = totalSlots > 0 && sucursalSeleccionada;

    const crearSlotsMasivos = async () => {
        if (!canSubmit) return;

        setCreando(true);
        setMensajeExito('');

        try {
            // Preparar los slots para el endpoint bulk
            const slots = previewSlots.map(slot => ({
                day_id: slot.diaId,
                hora: slot.hora
            }));
            console.log("slots a crear:", slots);
            console.log("sucursal seleccionada:", sucursalSeleccionada);
            console.log("observaciones:", observacionesModal);
            const response = await axios.post('/api/appointment_models_bulk', {
                sucursal_id: sucursalSeleccionada,
                slots: slots,
                observaciones: observacionesModal
            });

            const { total_creados, total_duplicados } = response.data;
            
            if (total_duplicados > 0) {
                setMensajeExito(`Se crearon ${total_creados} slots. ${total_duplicados} ya existían.`);
            } else {
                setMensajeExito(`Se crearon ${total_creados} slots exitosamente`);
            }

            setTimeout(() => {
                resetModal();
                if (diaSeleccionado > 0 && sucursalSeleccionada > 0) {
                    getCitas();
                }
            }, 2000);
        } catch (error) {
            console.error('Error al crear slots:', error);
            const message = error.response?.data?.message || 'Ocurrió un error al crear los slots';
            alert(message);
        } finally {
            setCreando(false);
        }
    };

    const resetModal = () => {
        setModalOpen(false);
        setDiaActivo(1);
        setHorasPorDia({});
        setHoraInput('');
        setObservacionesModal('');
        setMensajeExito('');
        setModoAutomatico(false);
        setHoraInicio('08:00');
        setHoraFin('18:00');
        setIntervalo(30);
        setShowCopiarMenu(false);
    };

    const horasDelDiaActivo = getHorasDelDia(diaActivo);

    return (
        <div>
            <div className="container-admin-menu">
                <Breadcrumb hasButton={false} hasFilters={false} />
            </div>
            <div className="container-admin-container">
                <div className="container-admin bg-white rounded-lg shadow-md p-4">

                    {/* Modal de Creación Masiva */}
                    <Transition show={modalOpen} as={Fragment}>
                        <Dialog onClose={() => {}} className="fixed z-50 inset-0 overflow-y-auto">
                            <div className="flex items-center justify-center min-h-screen px-4">
                                <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                                <DialogPanel className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-auto z-50 relative max-h-[90vh] overflow-y-auto">
                                    {/* Header */}
                                    <div className="sticky top-0 z-10 flex justify-between items-center border-b bg-gradient-to-r  from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl">
                                        <div className="flex items-center gap-3">
                                            <SparklesIcon className="h-6 w-6 text-white" />
                                            <DialogTitle className="text-lg font-semibold text-white">
                                                Definir Modelo de Citas
                                            </DialogTitle>
                                        </div>
                                        <button onClick={resetModal} className="text-white hover:text-gray-200">
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                    </div>

                                    {/* Body */}
                                    <div className="p-6 space-y-6">
                                        {/* Sucursal Info */}
                                        <div className="bg-indigo-50 rounded-lg p-4 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <CalendarDaysIcon className="h-8 w-8 text-blue-600" />
                                                <div>
                                                    <p className="text-sm text-blue-600 font-medium">Sucursal seleccionada</p>
                                                    <p className="text-lg font-bold text-blue-900">
                                                        {getSucursalNombre(sucursalSeleccionada) || 'Ninguna'}
                                                    </p>
                                                </div>
                                            </div>
                                            {diasConHorarios.length > 0 && (
                                                <div className="text-right">
                                                    <p className="text-sm text-blue-600">Total a crear</p>
                                                    <p className="text-2xl font-bold text-blue-900">{totalSlots} slots</p>
                                                </div>
                                            )}
                                        </div>

                                        {!sucursalSeleccionada && (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                                                ⚠️ Debes seleccionar una sucursal antes de definir el modelo de citas
                                            </div>
                                        )}

                                        {sucursalSeleccionada && (
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                                {/* Panel izquierdo: Selector de días */}
                                                <div className="lg:col-span-1 space-y-4">
                                                    <h4 className="font-semibold text-gray-800">Días de la semana</h4>
                                                    <div className="space-y-2">
                                                        {DIAS.map((dia) => {
                                                            const horasDia = getHorasDelDia(dia.id);
                                                            const isActive = diaActivo === dia.id;
                                                            return (
                                                                <button
                                                                    key={dia.id}
                                                                    type="button"
                                                                    onClick={() => setDiaActivo(dia.id)}
                                                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-all ${isActive
                                                                        ? 'bg-[#11cdef] text-white shadow-md'
                                                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                >
                                                                    <span className="font-medium">{dia.nombre}</span>
                                                                    {horasDia.length > 0 && (
                                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${isActive
                                                                            ? 'bg-white/20 text-white'
                                                                            : 'bg-blue-100 text-blue-700'
                                                                            }`}>
                                                                            {horasDia.length} horas
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Panel derecho: Configuración de horarios */}
                                                <div className="lg:col-span-2 space-y-4">
                                                    {/* Header del día */}
                                                    <div className="flex items-center justify-between">
                                                        <h4 className="font-semibold text-gray-800">
                                                            Horarios para <span className="text-blue-600">{DIAS.find(d => d.id === diaActivo)?.nombre}</span>
                                                        </h4>
                                                        <div className="flex items-center gap-2">
                                                            {/* Botón copiar */}
                                                            <div className="relative">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowCopiarMenu(!showCopiarMenu)}
                                                                    disabled={horasDelDiaActivo.length === 0}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <DocumentDuplicateIcon className="h-4 w-4" />
                                                                    Copiar a...
                                                                </button>
                                                                {showCopiarMenu && (
                                                                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border py-1 z-20">
                                                                        <button
                                                                            onClick={copiarATodosLosDias}
                                                                            className="w-full text-left px-4 py-2 text-sm text-blue-600 font-medium hover:bg-blue-50"
                                                                        >
                                                                            Copiar a todos
                                                                        </button>
                                                                        <div className="border-t my-1"></div>
                                                                        {DIAS.filter(d => d.id !== diaActivo).map(dia => (
                                                                            <button
                                                                                key={dia.id}
                                                                                onClick={() => copiarHorasADia(dia.id)}
                                                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                                                            >
                                                                                {dia.nombre}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {horasDelDiaActivo.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={limpiarHorasDia}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                                                                >
                                                                    <TrashIcon className="h-4 w-4" />
                                                                    Limpiar
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Toggle modo automático */}
                                                    <div className="bg-gray-50 rounded-lg p-4">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="flex items-center gap-2">
                                                                <BoltIcon className="h-5 w-5 text-amber-500" />
                                                                <span className="font-medium text-gray-700">Generar por intervalos</span>
                                                            </div>
                                                            <Switch
                                                                checked={modoAutomatico}
                                                                onChange={setModoAutomatico}
                                                                className={`${modoAutomatico ? 'bg-blue-600' : 'bg-gray-300'
                                                                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                                                            >
                                                                <span
                                                                    className={`${modoAutomatico ? 'translate-x-6' : 'translate-x-1'
                                                                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                                                />
                                                            </Switch>
                                                        </div>

                                                        {modoAutomatico ? (
                                                            <div className="space-y-4">
                                                                <div className="grid grid-cols-3 gap-3">
                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Hora inicio</label>
                                                                        <input
                                                                            type="time"
                                                                            value={horaInicio}
                                                                            onChange={(e) => setHoraInicio(e.target.value)}
                                                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Hora fin</label>
                                                                        <input
                                                                            type="time"
                                                                            value={horaFin}
                                                                            onChange={(e) => setHoraFin(e.target.value)}
                                                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Intervalo</label>
                                                                        <select
                                                                            value={intervalo}
                                                                            onChange={(e) => setIntervalo(parseInt(e.target.value))}
                                                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 browser-default"
                                                                        >
                                                                            {INTERVALOS.map(int => (
                                                                                <option key={int.value} value={int.value}>{int.label}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={generarHorariosAutomaticos}
                                                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
                                                                >
                                                                    <BoltIcon className="h-5 w-5" />
                                                                    Generar horarios
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex gap-2">
                                                                <div className="relative flex-1">
                                                                 
                                                                    <input
                                                                        type="time"
                                                                        value={horaInput}
                                                                        onChange={(e) => setHoraInput(e.target.value)}
                                                                        onKeyDown={(e) => e.key === 'Enter' && agregarHora()}
                                                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm dento-input focus:ring-2 focus:ring-blue-500"
                                                                    />
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={agregarHora}
                                                                    disabled={!horaInput}
                                                                    className="inline-flex items-center gap-2 px-4 py-2.5 ml-2.5 bg-[#11cdef] text-white rounded-lg text-sm font-medium hover:bg-[#0ea5e9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                                >
                                                                    <PlusIcon className="h-5 w-5" />
                                                                    Agregar
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Horas del día actual */}
                                                    <div className="min-h-[120px]">
                                                        {horasDelDiaActivo.length > 0 ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {horasDelDiaActivo.map((hora) => (
                                                                    <span
                                                                        key={hora}
                                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-100 text-gray-800 rounded-full text-sm font-medium"
                                                                    >
                                                                        <ClockIcon className="h-4 w-4" />
                                                                        {hora}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => eliminarHora(hora)}
                                                                            className="ml-1 text-blue-600 hover:text-red-600 transition-colors"
                                                                        >
                                                                            <XMarkIcon className="h-4 w-4" />
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center justify-center h-[120px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                                                <p className="text-gray-500 text-sm">
                                                                    No hay horarios para este día
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Observaciones */}
                                        {/*sucursalSeleccionada && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Observaciones (opcional)
                                                </label>
                                                <textarea
                                                    value={observacionesModal}
                                                    onChange={(e) => setObservacionesModal(e.target.value)}
                                                    rows={2}
                                                    placeholder="Notas adicionales para estos slots..."
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                        )*/}

                                        {/* Resumen por días */}
                                        {diasConHorarios.length > 0 && (
                                            <div className="bg-gradient-to-br from-gray-50 to-indigo-50 rounded-xl p-4 border border-indigo-100">
                                                <h4 className="font-semibold text-gray-800 mb-3">Resumen de slots a crear</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    {diasConHorarios.map(dia => {
                                                        const horas = getHorasDelDia(dia.id);
                                                        return (
                                                            <div key={dia.id} className="bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-100">
                                                                <p className="text-xs text-blue-600 font-medium">{dia.nombre}</p>
                                                                <p className="text-lg font-bold text-gray-800">{horas.length} slots</p>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Mensaje de éxito */}
                                        {mensajeExito && (
                                            <div className="bg-green-100 text-green-800 rounded-lg px-4 py-3 flex items-center gap-2">
                                                <CheckIcon className="h-5 w-5" />
                                                {mensajeExito}
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div className="sticky bottom-0 flex justify-end gap-3 bg-gray-50 px-6 py-4 rounded-b-xl border-t">
                                        <button
                                            type="button"
                                            onClick={resetModal}
                                            disabled={creando}
                                            className="px-5 py-2.5 bg-[#f5365c] border border-gray-300 text-white rounded-lg hover:bg-gray-50 hover:text-[#f5365c] transition-colors font-medium cursor-pointer"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={crearSlotsMasivos}
                                            disabled={!canSubmit || creando}
                                            className="px-5 py-2.5 bg-[#11cdef] text-white rounded-lg hover:bg-[#0ea5e9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium inline-flex items-center gap-2 cursor-pointer"
                                        >
                                            {creando ? (
                                                <>
                                                    <Spinner /> Creando...
                                                </>
                                            ) : (
                                                <>
                                                    <SparklesIcon className="h-5 w-5" />
                                                    Crear {totalSlots} slots
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </DialogPanel>
                            </div>
                        </Dialog>
                    </Transition>

                    {loadingPermisos ? (
                        <Loader />
                    ) : tienePermiso('appointment_models_list_read') ? (
                        <>
                            {/* Header con filtros */}
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 info-badge rounded-lg p-4 mb-4">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                    {/* Selector de día */}
                                    <div className="w-full sm:w-48">
                                        <label className="block text-xs font-base text-white mb-1">Tipo de Día</label>
                                        <select
                                            value={diaSeleccionado}
                                            onChange={(e) => setDiaSeleccionado(parseInt(e.target.value))}
                                            className="w-full browser-default dento-input"
                                        >
                                            <option value="0" disabled>Seleccione un día</option>
                                            {DIAS.map((dia) => (
                                                <option value={dia.id} key={dia.id}>{dia.nombre}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Selector de sucursal */}
                                    <div className="w-full sm:w-64">
                                        <label className="block text-xs font-base text-white mb-1">Sucursal</label>
                                        <select
                                            value={sucursalSeleccionada}
                                            onChange={(e) => setSucursalSeleccionada(e.target.value)}
                                            className="w-full browser-default dento-input"
                                        >
                                            <option value="" disabled>Seleccione la sucursal</option>
                                            {sucursales.map((sucursal) => (
                                                <option value={sucursal.id} key={sucursal.id}>{sucursal.nombre_sucursal}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Botón para abrir modal de creación masiva */}
                                {tienePermiso('appointment_models_create') && (
                                    <button
                                        onClick={() => setModalOpen(true)}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#11cdef] text-white rounded-lg text-sm font-medium hover:bg-[#0ea5e9] transition-colors shadow-sm cursor-pointer"
                                    >
                                        <SparklesIcon className="h-5 w-5" />
                                        Definir Modelo de Citas
                                    </button>
                                )}
                            </div>

                            {/* Contenido principal */}
                            {diaSeleccionado > 0 && sucursalSeleccionada > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full min-h-50 divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Sucursal
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Hora
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Observaciones
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Día
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Acciones
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={5} className="px-4 py-8">
                                                        <div className="flex justify-center">
                                                            <Spinner />
                                                        </div>
                                                        <p className='text-center text-base text-gray-400 p-5 mt-2.5'>Cargando Modelos de Citas...</p>
                                                    </td>
                                                </tr>
                                            ) : citas.length > 0 ? (
                                                citas.map((cita) => (
                                                    <tr key={cita.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                            {getSucursalNombre(cita.sucursal_id)}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <input
                                                                type="time"
                                                                value={cita.hora || ''}
                                                                onChange={(e) => handleInputChange('hora', e.target.value, cita.id)}
                                                                onBlur={(e) => handleBlur('hora', e.target.value, cita)}
                                                                disabled={!tienePermiso('appointment_models_update')}
                                                                className="dento-input w-full"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="text"
                                                                value={cita.observaciones || ''}
                                                                onChange={(e) => handleInputChange('observaciones', e.target.value, cita.id)}
                                                                onBlur={(e) => handleBlur('observaciones', e.target.value, cita)}
                                                                disabled={!tienePermiso('appointment_models_update')}
                                                                placeholder="Sin observaciones"
                                                                className="w-full dento-input"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-300 text-blue-600">
                                                                {DIAS.find(d => d.id === cita.day_id)?.nombre || ''}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            {tienePermiso('appointment_models_delete') && (
                                                                <button
                                                                    onClick={() => handleDelete(cita)}
                                                                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                                                    title="Eliminar"
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                                        No hay horarios definidos para este día y sucursal
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-8 text-center">
                                    <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                                    <p className="text-gray-600 font-medium">
                                        Selecciona un día y una sucursal para ver el modelo de citas
                                    </p>
                                </div>
                            )}
                        </>
                    ) : (
                        <NoPermissions />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModeloCitas;